import { ColDef, GetRowIdParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { useCommonCodeStore } from 'src/store/useCommonCodeStore';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { getDefaultRowIndexColumnConfig, getRowStyle } from 'src/utils/grid/gridStyleUtils';
import { defaultEQM1011_R01Data, EQM1011_R01Dto } from './Dto_EQM1011_R01';
import { useEQM1011_R01Store } from './Store_EQM1011_R01';

const COLUMN_STATE_KEY = 'EQM1011_R01-grid-column-state';
const EMPTY_SELECTED_ROWS: string[] = [];
const HEADER_NAMES: Record<string, string> = {
    rowIndex: '',
    compCd: '사업장',
    compNm: '사업장명',
    plcid: 'PLC 번호',
    plcName: 'PLC명',
    plcaddressid: '데이터종류',
    plcAddressName: '데이터명',
    value: '수집데이터값',
    createdatetime: '수집일시',
};

export const Table_EQM1011_R01 = () => {
    const gridRef = useRef<AgGridReact<EQM1011_R01Dto>>(null);
    const {
        EQM1011_R01List,
        columnState,
        setColumnState,
        scrollTop,
        setScrollTop,
    } = useEQM1011_R01Store();
    const { compCdList } = useCommonCodeStore();
    const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

    const { createRowClassRules, paddedGridData } = useGridSettingContent({
        gridRef,
        key: 'valueIndex',
        gridData: EQM1011_R01List,
        defaultGridData: defaultEQM1011_R01Data,
        selectedRow: EMPTY_SELECTED_ROWS,
    });

    const getEQM1011_R01RowId = useCallback((params: GetRowIdParams<EQM1011_R01Dto>) => (
        params.data.id > 0
            ? String(params.data.id)
            : `placeholder-${params.data.valueIndex}`
    ), []);

    const companyMap = useMemo(
        () => new Map(compCdList.map((item) => [item.compCd, item.compNm])),
        [compCdList],
    );
    const getCellStyle = useCallback(
        (params: unknown, key: string) => onlyTouchGridStyle(key, 'compCd')(params),
        [],
    );

    const baseColumnDefs: ColDef<EQM1011_R01Dto>[] = useMemo(() => [
        ...getDefaultRowIndexColumnConfig(),
        {
            headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'compCd'),
        },
        {
            headerName: '사업장명', colId: 'compNm', field: 'compNm', width: 150,
            headerClass: aggridHeaderClass,
            valueGetter: (params) => companyMap.get(params.data?.compCd ?? '')
                || params.data?.compNm
                || '',
            cellStyle: (params) => getCellStyle(params, 'compNm'),
        },
        {
            headerName: 'PLC 번호', colId: 'plcid', field: 'plcid', width: 90,
            headerClass: aggridHeaderClass,
            type: 'numericColumn',
            cellStyle: (params) => getCellStyle(params, 'plcid'),
        },
        {
            headerName: 'PLC명', colId: 'plcName', field: 'plcName', width: 120,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'plcName'),
        },
        {
            headerName: '데이터종류', colId: 'plcaddressid', field: 'plcaddressid', width: 110,
            headerClass: aggridHeaderClass,
            type: 'numericColumn',
            cellStyle: (params) => getCellStyle(params, 'plcaddressid'),
        },
        {
            headerName: '데이터명', colId: 'plcAddressName', field: 'plcAddressName', width: 140,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'plcAddressName'),
        },
        {
            headerName: '수집데이터값', colId: 'value', field: 'value', width: 120,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'value'),
        },
        {
            headerName: '수집일시', colId: 'createdatetime', field: 'createdatetime', width: 160,
            headerClass: aggridHeaderClass,
            cellStyle: (params) => getCellStyle(params, 'createdatetime'),
        },
    ], [companyMap, getCellStyle]);

    const defaultColumnState = useMemo(
        () => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
        [baseColumnDefs],
    );
    const columnDefs = useMemo(() => {
        if (columnState === null) return baseColumnDefs;
        return GridCustomColumnDefs<EQM1011_R01Dto>({
            baseColumnDefs,
            columnState,
            headerNames: HEADER_NAMES,
            fallbackHeaderClass: aggridHeaderClass,
        });
    }, [baseColumnDefs, columnState]);
    const pinnedBottomRow = useMemo(() => ([{
        rowIndex: '',
        compCd: `${EQM1011_R01List.length.toLocaleString()}건`,
        editable: false,
    }]), [EQM1011_R01List]);

    return (
        <div className="flex flex-col gap-3 h-full min-h-0">
            <GridContextWrapper<EQM1011_R01Dto>
                gridRef={gridRef}
                paddedGridData={paddedGridData}
                enableCellCalculation
                initSelected={() => {}}
                defaultRowData={defaultEQM1011_R01Data}
                baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
                defaultColumnState={defaultColumnState}
                setColumnState={setColumnState}
                localStorageKey={COLUMN_STATE_KEY}
                menuItems={undefined}
            >
                <Grid_Touch<EQM1011_R01Dto>
                    ref={gridRef}
                    rowData={paddedGridData}
                    columnDefs={columnDefs}
                    getRowId={getEQM1011_R01RowId}
                    onCellKeyDown={undefined}
                    pinnedBottomRowData={pinnedBottomRow}
                    rowClassRules={createRowClassRules<EQM1011_R01Dto>({
                        idKey: 'id',
                        selectedList: EMPTY_SELECTED_ROWS,
                        deletedList: [],
                    })}
                    suppressRowClickSelection={true}
                    animateRows={true}
                    enableCellTextSelection={true}
                    ensureDomOrder={false}
                    getRowStyle={getRowStyle}
                    setHoveredRowIndex={setHoveredRowIndex}
                    columnStateKey={COLUMN_STATE_KEY}
                    setColumnState={setColumnState}
                    scrollTop={scrollTop}
                    setScrollTop={setScrollTop}
                />
            </GridContextWrapper>
        </div>
    );
};
